import { Injectable, NgZone } from '@angular/core';
import { KeycloakService } from 'keycloak-angular';
import {
    StreamingException,
    StreamingAuthenticationException,
    StreamingConnectionException,
} from '../exceptions/streaming-exceptions';

interface RetryConfig {
    maxRetries: number;
    initialDelayMs: number;
    maxDelayMs: number;
    backoffMultiplier: number;
}

@Injectable({
    providedIn: 'root'
})
export class StreamingService {
    private retryConfig: RetryConfig = {
        maxRetries: 5,
        initialDelayMs: 1000,
        maxDelayMs: 30000,
        backoffMultiplier: 2
    };
    private isClosed = false;
    private retryCount = 0;
    private abortController: AbortController | null = null;
    private lastMessageTime: number = 0;
    private connectionStartTime: number = 0;
    private keepAliveTimer: any = null;
    private lastEventId: string | null = null;
    private readonly STALE_CONNECTION_TIMEOUT = 150000; // 2.5 minutes without any data = stale

    constructor(private readonly zone: NgZone, private readonly keycloakService: KeycloakService) { }

    async connect<T>(url: string, onMessage: (msg: T) => void, onError?: (error: StreamingException) => void) {
        this.isClosed = false;
        this.retryCount = 0;
        this.lastEventId = null;
        this.abortController = new AbortController();

        const attemptConnection = async (): Promise<void> => {
            try {
                const token = await this.keycloakService.getToken();
                if (!token || token.trim() === '') {
                    const authError = new StreamingAuthenticationException('Failed to obtain authentication token');
                    if (onError && !this.isClosed) this.zone.run(() => onError(authError));
                    return;
                }

                await this.connectWithRetry(url, token, onMessage, onError, attemptConnection);
            } catch (tokenError: any) {
                const authError = new StreamingAuthenticationException('Token retrieval error', tokenError);
                if (onError && !this.isClosed) this.zone.run(() => onError(authError));
            }
        };

        try {
            await attemptConnection();
        } catch (error) {
            const connectionError = new StreamingConnectionException('Connection attempt failed', error);
            if (onError && !this.isClosed) this.zone.run(() => onError(connectionError));
        }

        return () => {
            this.isClosed = true;
            if (this.abortController) {
                this.abortController.abort();
                this.abortController = null;
            }
        };
    }

    private async connectWithRetry<T>(
        url: string,
        token: string,
        onMessage: (msg: T) => void,
        onError: ((error: StreamingException) => void) | undefined,
        retryCallback: () => Promise<void>
    ): Promise<void> {
        if (this.isClosed) return;

        // Refresh token if expired before connection attempt (similar to auth.interceptor.ts)
        let currentToken = token;
        try {
            if (this.keycloakService.isTokenExpired()) {
                const refresh_time = 30;
                await this.keycloakService.updateToken(refresh_time);
                const refreshedToken = await this.keycloakService.getToken();
                if (refreshedToken) {
                    currentToken = refreshedToken;
                }
            }
        } catch (refreshError) {
            console.warn('[Streaming] Token refresh failed, proceeding with existing token:', refreshError);
            // Continue with existing token - let the connection attempt handle auth errors
        }

        this.abortController = new AbortController();

        // Build headers object
        const headers: HeadersInit = {
            'Authorization': `Bearer ${currentToken}`,
            'Accept': 'text/event-stream',
            'Cache-Control': 'no-cache'
        };
        
        // Include Last-Event-ID header if we have a previous event ID (for resumption)
        if (this.lastEventId) {
            headers['Last-Event-ID'] = this.lastEventId;
        }

        try {
            const response = await fetch(url, {
                method: 'GET',
                headers: headers,
                mode: 'cors',
                credentials: 'include',
                signal: this.abortController.signal
            });

            await this.handleFetchResponse(response, onMessage, retryCallback, onError);
        } catch (fetchError: any) {
            this.handleFetchError(fetchError, retryCallback, onError);
        }
    }

    private async handleFetchResponse<T>(
        response: Response,
        onMessage: (msg: T) => void,
        retryCallback: () => Promise<void>,
        onError: ((error: StreamingException) => void) | undefined
    ): Promise<void> {
        if (!response.ok) {
            if (!this.isClosed) {
                this.scheduleRetry(retryCallback, onError);
            }
            return;
        }

        // Initialize connection
        this.retryCount = 0;
        this.connectionStartTime = Date.now();
        this.lastMessageTime = Date.now();
        this.startKeepAliveMonitor();

        if (!response.body) {
            return;
        }

        await this.processStreamData(response.body, onMessage);

        // Handle connection end
        const connectionDuration = (Date.now() - this.connectionStartTime) / 1000;
        this.stopKeepAliveMonitor();

        // Auto-reconnect on server timeout (3-minute timeout detection)
        if (connectionDuration >= 175 && connectionDuration <= 185 && !this.isClosed) {
            this.scheduleRetry(retryCallback, onError);
        }
    }

    private async processStreamData<T>(
        body: ReadableStream<Uint8Array>,
        onMessage: (msg: T) => void
    ): Promise<void> {
        const reader = body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (!this.isClosed) {
            const { done, value } = await reader.read();

            if (done) {
                this.stopKeepAliveMonitor();
                break;
            }

            this.lastMessageTime = Date.now();
            buffer += decoder.decode(value, { stream: true });
            buffer = this.processSSEMessages(buffer, onMessage);
        }
    }

    private processSSEMessages<T>(buffer: string, onMessage: (msg: T) => void): string {
        const parts = buffer.split("\n\n");
        const remainingBuffer = parts.pop()!;

        for (const part of parts) {
            this.processSSEPart(part, onMessage);
        }

        return remainingBuffer;
    }

    private processSSEPart<T>(part: string, onMessage: (msg: T) => void): void {
        const lines = part.split('\n');
        let eventId: string | undefined;
        let eventData: string | undefined;
        
        for (const line of lines) {
            const trimmedLine = line.trim();

            // Handle heartbeat/comment messages (keeps connection alive)
            if (trimmedLine.startsWith(":")) {
                continue;
            }
            
            // Handle event ID
            if (trimmedLine.startsWith("id:")) {
                eventId = trimmedLine.replace("id:", "").trim();
                // Store the last event ID for potential reconnection
                if (eventId) {
                    this.lastEventId = eventId;
                }
                continue;
            }

            // Handle data messages
            if (trimmedLine.startsWith("data:")) {
                eventData = trimmedLine.replace("data:", "").trim();
            }
        }
        
        // Process the event data if present
        if (eventData) {
            try {
                const parsedData = JSON.parse(eventData);
                // Attach the event ID if present
                if (eventId) {
                    parsedData.id = eventId;
                }
                this.zone.run(() => onMessage(parsedData));
            } catch (parseError) {
                // Parse error - skip malformed message
            }
        }
    }

    private handleFetchError(
        fetchError: any,
        retryCallback: () => Promise<void>,
        onError: ((error: StreamingException) => void) | undefined
    ): void {
        this.stopKeepAliveMonitor();

        if (fetchError.name === 'AbortError') {
            return;
        }

        if (!this.isClosed) {
            this.scheduleRetry(retryCallback, onError);
        }
    }

    private scheduleRetry(retryCallback: () => Promise<void>, onError?: (error: StreamingException) => void): void {
        // Don't schedule retry if connection was manually closed
        if (this.isClosed) {
            return;
        }

        if (this.retryCount >= this.retryConfig.maxRetries) {
            // Maximum retries exhausted - throw custom exception
            if (onError) {
                const connectionError = new StreamingConnectionException(
                    `Connection failed after ${this.retryConfig.maxRetries} retry attempts`,
                    { maxRetries: this.retryConfig.maxRetries, retryCount: this.retryCount }
                );
                this.zone.run(() => onError(connectionError));
            }
            return;
        }

        const delayMs = Math.min(
            this.retryConfig.initialDelayMs * Math.pow(this.retryConfig.backoffMultiplier, this.retryCount),
            this.retryConfig.maxDelayMs
        );

        this.retryCount++;
        setTimeout(() => {
            if (!this.isClosed) {
                retryCallback();
            }
        }, delayMs);
    }

    /**
     * Disconnects the streaming connection and stops listening to events
     * This will abort any ongoing fetch requests and prevent reconnection attempts
     */
    disconnect(): void {
        this.isClosed = true;
        this.retryCount = 0;
        this.lastEventId = null;
        this.stopKeepAliveMonitor();

        if (this.abortController) {
            this.abortController.abort();
            this.abortController = null;
        }
    }

    /**
     * Monitors connection health and detects stale connections
     * If no data is received for STALE_CONNECTION_TIMEOUT, connection is considered dead
     */
    private startKeepAliveMonitor(): void {
        this.stopKeepAliveMonitor(); // Clear any existing timer
        
        this.keepAliveTimer = setInterval(() => {
            const timeSinceLastMessage = Date.now() - this.lastMessageTime;
            
            if (timeSinceLastMessage > this.STALE_CONNECTION_TIMEOUT) {
                const connectionDuration = (Date.now() - this.connectionStartTime) / 1000;
                console.warn(`[Streaming] Connection appears stale (${(timeSinceLastMessage / 1000).toFixed(1)}s since last data, ${connectionDuration.toFixed(1)}s total). Server may not be sending heartbeats.`);
                console.warn('[Streaming] Recommend configuring backend to send heartbeat comments every 30-60 seconds');
                // Note: We don't auto-reconnect here because the workflow might just be slow
                // The actual disconnect will happen when the server times out
            }
        }, 30000); // Check every 30 seconds
    }

    private stopKeepAliveMonitor(): void {
        if (this.keepAliveTimer) {
            clearInterval(this.keepAliveTimer);
            this.keepAliveTimer = null;
        }
    }
}
